import { useEffect, useRef } from 'react'

import { Input } from '@/shared/components/ui/input'
import { cn } from '@/shared/lib/utils'

export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  autoFocus = false,
}) {
  const inputsRef = useRef([])

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus()
    }
  }, [autoFocus])

  const digits = Array.from({ length }, (_, index) => value[index] || '')

  const updateValue = (nextDigits) => {
    onChange(nextDigits.join('').slice(0, length))
  }

  const handleChange = (index, event) => {
    const raw = event.target.value.replace(/\D/g, '')
    if (!raw) {
      const next = [...digits]
      next[index] = ''
      updateValue(next)
      return
    }

    const chars = raw.split('')
    const next = [...digits]

    chars.forEach((char, offset) => {
      const target = index + offset
      if (target < length) {
        next[target] = char
      }
    })

    updateValue(next)
    inputsRef.current[Math.min(index + chars.length, length - 1)]?.focus()
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      inputsRef.current[index - 1]?.focus()
    }
    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault()
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handlePaste = (event) => {
    event.preventDefault()
    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length)

    if (!pasted) return

    const next = Array.from({ length }, (_, index) => pasted[index] || '')
    updateValue(next)
    inputsRef.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  return (
    <div
      className="flex w-full gap-2 sm:gap-2.5"
      role="group"
      aria-label="One-time password"
    >
      {digits.map((digit, index) => (
        <Input
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el
          }}
          value={digit}
          disabled={disabled}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          inputMode="numeric"
          maxLength={1}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${index + 1}`}
          className={cn(
            'aspect-square h-auto max-h-12 flex-1 px-0 text-center text-lg font-semibold sm:max-h-14 sm:text-xl',
          )}
        />
      ))}
    </div>
  )
}
